import React from 'react'
import { useTranslation } from 'lib/i18n'
import querystring from 'querystring'

import CtaButton from 'components/CtaButton/CtaButton'
import DiscountStarBadge from 'components/DiscountStarBadge/DiscountStarBadge'
import Link from 'components/Link/Link'

import { BookButtonProps, BookButtonTextProps, NodeProps } from '../type'

import styles from './BookButton.module.css'

const BookButtonText = ({ price, discount }: BookButtonTextProps) => {
  const { t } = useTranslation()

  if (discount) {
    const text = t('AddToCartButton.ctaGenericProductMobile', 'Buy for')
    return (
      <>
        {text}
        <span className={styles.crossedOutPrice}>{price}</span>
        <strong>{discount}</strong>
      </>
    )
  }

  return <>{t('BookButtons.bookB2c', 'Book from {{price}}', { price })}</>
}

const BookButton: React.FC<BookButtonProps> = ({
  products,
  onClick,
  profileSlug,
  defaultProfileProduct,
  discountBadgeColor,
  loading = false,
}) => {
  let highestPrioB2cProduct: NodeProps
  if (products?.b2c?.length) {
    highestPrioB2cProduct = products?.b2c?.filter(
      (node: NodeProps) => node?.product?.productType !== 'DONATION',
    )[0]
  } else {
    highestPrioB2cProduct = defaultProfileProduct
  }
  if (!highestPrioB2cProduct && !loading) return null

  const hasOneB2cProduct = products?.b2c?.length === 1

  const onBookButtonClick = () => !hasOneB2cProduct && onClick()

  const couponCode = highestPrioB2cProduct?.discount?.couponCode

  const queryParams: { couponCode?: string } = {}
  if (couponCode) {
    queryParams.couponCode = couponCode
  }

  const queryString = querystring.stringify(queryParams)

  let href = `/profile/${profileSlug}/product/${highestPrioB2cProduct?.product?.slug}`

  if (queryString) {
    href = `${href}?${queryString}`
  }

  type ButtonWrapperProps = {
    children: React.ReactNode
  }

  const ButtonWrapper = ({ children }: ButtonWrapperProps) => {
    if (hasOneB2cProduct) {
      return (
        <Link href={href}>
          <a className={styles.buttonLink} rel="nofollow">
            {children}
          </a>
        </Link>
      )
    }

    return <>{children}</>
  }

  return (
    <>
      <ButtonWrapper>
        <CtaButton
          className={styles.bookButton}
          size="large"
          data-cy="profilePageB2cBookButton"
          onClick={onBookButtonClick}
        >
          {!defaultProfileProduct ? (
            <div className={styles.loading} />
          ) : (
            <>
              <BookButtonText
                price={highestPrioB2cProduct?.price?.formattedValue}
                discount={
                  highestPrioB2cProduct?.discount?.discountedPrice
                    ?.formattedValue
                }
              />

              <DiscountStarBadge
                discount={highestPrioB2cProduct?.discount}
                backgroundColor={
                  discountBadgeColor === 'light'
                    ? '#fff'
                    : 'var(--colors-secondary-900)'
                }
                style={{
                  top: -22,
                  right: -15,
                }}
              />
            </>
          )}
        </CtaButton>
      </ButtonWrapper>
    </>
  )
}

export default BookButton
